import React, { useState, useEffect } from 'react';
import { Check, Zap, Crown, Mail, Users, Star } from 'lucide-react';
import { AuthForm } from '../components/AuthForm';
import { supabase } from '../lib/supabase';
import { stripeService } from '../lib/stripeService';
import { userTierService, PROSUMER_ENABLED } from '../lib/userTierService';
import { track } from '../utils/analytics';
import {
  PRO_MONTHLY,
  PRO_ANNUAL_TOTAL,
  PRO_ANNUAL_PER_MONTH,
  PRO_ANNUAL_SAVINGS_PCT,
  PROSUMER_MONTHLY,
  PROSUMER_ANNUAL_TOTAL,
  PROSUMER_ANNUAL_PER_MONTH,
  PROSUMER_ANNUAL_SAVINGS_PCT,
} from '../config/proPricing';

export const PricingPage: React.FC = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [contactPlan, setContactPlan] = useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = useState(false);
  // 'paid'/'pro'/'enterprise' means the user already has Pro-or-better; the
  // upgrade CTA must become "Manage subscription" so they can't start a second
  // subscription (double billing). null until resolved.
  const [isPro, setIsPro] = useState<boolean | null>(null);
  const [isProsumer, setIsProsumer] = useState<boolean>(false);
  // If an anonymous user clicks a paid CTA, remember which plan + cycle so we
  // can resume checkout after they authenticate instead of dumping them home.
  const [pendingCheckout, setPendingCheckout] =
    useState<{ cycle: 'monthly' | 'annual'; plan: 'pro' | 'prosumer' } | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      if (!user) {
        setIsPro(false);
        return;
      }
      userTierService
        .getUserTier()
        .then((tier) => {
          setIsPro(tier === 'paid' || tier === 'pro' || tier === 'enterprise');
          setIsProsumer(tier === 'prosumer');
        })
        .catch(() => setIsPro(false));
    });
  }, []);

  const handleGetStartedFree = () => {
    track('signup_started', { source: 'pricing' });
    setShowAuthModal(true);
  };

  const openBillingPortal = async () => {
    setIsLoading(true);
    try {
      const url = await stripeService.createPortalSession();
      if (url) window.location.href = url;
      else throw new Error('No portal URL returned');
    } catch (error) {
      console.error('Error opening billing portal:', error);
      setShowStripeModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const startCheckout = async (
    cycle: 'monthly' | 'annual',
    plan: 'pro' | 'prosumer' = 'pro',
  ) => {
    setIsLoading(true);
    try {
      track('checkout_started', { plan, billing_cycle: cycle, authenticated: true });
      const { url } = await stripeService.createCheckoutSession(cycle, plan);
      if (url) {
        track('checkout_redirected', { plan, billing_cycle: cycle });
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      track('checkout_failed', { plan, billing_cycle: cycle });
      setShowStripeModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // Resume the exact plan the user came here to buy, rather than redirecting
    // home and abandoning the checkout.
    if (pendingCheckout) {
      const { cycle, plan } = pendingCheckout;
      setPendingCheckout(null);
      startCheckout(cycle, plan);
      return;
    }
    window.location.href = '/';
  };

  const handleUpgrade = async (
    cycle: 'monthly' | 'annual',
    plan: 'pro' | 'prosumer' = 'pro',
  ) => {
    // Already on this plan (or better) — manage it instead of double-subscribing.
    if (plan === 'pro' && isPro) return openBillingPortal();
    if (plan === 'prosumer' && isProsumer) return openBillingPortal();
    if (!user) {
      track('checkout_started', { plan, billing_cycle: cycle, authenticated: false });
      setPendingCheckout({ cycle, plan });
      setShowAuthModal(true);
      return;
    }
    return startCheckout(cycle, plan);
  };

  const SALES_EMAIL = 'sales@commsgenie.com';

  // Open a modal instead of firing a bare mailto: — a mailto with no OS/browser
  // mail handler silently does nothing, which made "Contact Sales" look broken.
  // The modal always gives the user the address (mailto link + copy button).
  const handleSalesContact = (plan: string) => {
    track('sales_contact_started', { source: 'pricing', plan });
    setCopiedEmail(false);
    setContactPlan(plan);
  };

  const mailtoHref = (plan: string) =>
    'mailto:' + SALES_EMAIL +
    '?subject=' + encodeURIComponent(plan + ' Plan Inquiry') +
    '&body=' + encodeURIComponent("Hi, I'm interested in the " + plan + ' plan for my team.');

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SALES_EMAIL);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } catch {
      /* clipboard unavailable — the address is visible on the button regardless */
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:px-8">

      {/* Header */}
      <div className="mb-10 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Simple, Transparent Pricing</h2>
        <p className="text-gray-500 text-sm max-w-xl mx-auto">
          AI comment summaries are the only gated resource. Docket tracking, watchlist alerts,
          and bulk download are free for everyone.
        </p>
      </div>

      {/* Billing cycle toggle */}
      <div className="mb-8 flex justify-center">
        <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => setBillingCycle('monthly')}
            aria-pressed={billingCycle === 'monthly'}
            className={`rounded-full px-5 py-1.5 text-sm font-medium transition-colors ${
              billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle('annual')}
            aria-pressed={billingCycle === 'annual'}
            className={`flex items-center gap-2 rounded-full px-5 py-1.5 text-sm font-medium transition-colors ${
              billingCycle === 'annual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Yearly
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
              Save {PRO_ANNUAL_SAVINGS_PCT}%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className={`grid grid-cols-1 gap-5 mx-auto ${PROSUMER_ENABLED ? 'md:grid-cols-2 lg:grid-cols-4 max-w-6xl' : 'md:grid-cols-3 max-w-5xl'}`}>

        {/* Free */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 mb-4">
              <Zap className="w-5 h-5 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Free</h3>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-3xl font-bold text-gray-900">$0</span>
              <span className="text-gray-400 text-sm">/month</span>
            </div>
            <p className="text-gray-400 text-xs">No credit card required.</p>
          </div>

          <ul className="space-y-2.5 mb-8 flex-1">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-800 text-sm font-medium">{PROSUMER_ENABLED ? '2' : '5'} AI comment summaries/month</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 text-sm">Unlimited docket search</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 text-sm">Follow up to 5 dockets, with alerts</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 text-sm">Bulk filing download</span>
            </li>
          </ul>

          <button
            onClick={handleGetStartedFree}
            className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            Get Started Free
          </button>
        </div>

        {/* Prosumer — Most Popular (gated on live-price availability) */}
        {PROSUMER_ENABLED && (
        <div className="relative bg-white border-2 border-green-500 rounded-2xl p-6 flex flex-col shadow-sm">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white text-[11px] font-semibold px-3 py-1 rounded-full">
            Most Popular
          </span>
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-green-50 mb-4">
              <Star className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Prosumer</h3>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-3xl font-bold text-gray-900">
                ${billingCycle === 'annual' ? PROSUMER_ANNUAL_PER_MONTH : PROSUMER_MONTHLY}
              </span>
              <span className="text-gray-400 text-sm">/month</span>
            </div>
            <p className="text-gray-400 text-xs">
              {billingCycle === 'annual'
                ? `Billed annually ($${PROSUMER_ANNUAL_TOTAL}/yr) · save ${PROSUMER_ANNUAL_SAVINGS_PCT}%`
                : 'For staying on top of the dockets you care about.'}
            </p>
          </div>

          <ul className="space-y-2.5 mb-8 flex-1">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-800 text-sm font-medium">5 cited AI comment summaries/month</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-800 text-sm font-medium">Follow up to 25 dockets, with alerts</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 text-sm">Research tool (limited)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 text-sm">Everything in Free</span>
            </li>
          </ul>

          <button
            onClick={() => handleUpgrade(billingCycle, 'prosumer')}
            disabled={isLoading || isPro === true}
            className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50"
          >
            {isLoading
              ? 'Loading...'
              : isPro
                ? 'Included in Pro'
                : isProsumer
                  ? 'Manage subscription'
                  : 'Get Prosumer'}
          </button>
        </div>
        )}

        {/* Pro */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-green-50 mb-4">
              <Crown className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Pro</h3>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-3xl font-bold text-gray-900">
                ${billingCycle === 'annual' ? PRO_ANNUAL_PER_MONTH : PRO_MONTHLY}
              </span>
              <span className="text-gray-400 text-sm">/month</span>
            </div>
            <p className="text-gray-400 text-xs">
              {billingCycle === 'annual'
                ? `Billed annually ($${PRO_ANNUAL_TOTAL.toLocaleString()}/yr) · save ${PRO_ANNUAL_SAVINGS_PCT}%`
                : 'For individual telecom attorneys.'}
            </p>
          </div>

          <ul className="space-y-2.5 mb-8 flex-1">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-800 text-sm font-medium">Unlimited AI comment summaries</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-800 text-sm font-medium">Unlimited watched dockets</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 text-sm">Everything in Free</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 text-sm">Workspaces & team collaboration</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 text-sm">Priority summary processing</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 text-sm">Email support</span>
            </li>
          </ul>

          <button
            onClick={() => handleUpgrade(billingCycle, 'pro')}
            disabled={isLoading}
            className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm disabled:opacity-50"
          >
            {isLoading
              ? 'Loading...'
              : isPro
                ? 'Manage subscription'
                : 'Upgrade to Pro'}
          </button>
        </div>

        {/* Firm - Best Value */}
        <div className="relative bg-gray-900 border border-gray-900 rounded-2xl p-6 flex flex-col">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
              Best Value
            </span>
          </div>

          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 mb-4">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-1">Firm</h3>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-sm text-gray-400 font-normal">from</span>
              <span className="text-3xl font-bold text-white">$100</span>
              <span className="text-gray-400 text-sm">/seat/month</span>
            </div>
            <p className="text-gray-400 text-xs">Volume pricing for practices of 10+ attorneys.</p>
          </div>

          <ul className="space-y-2.5 mb-8 flex-1">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-white text-sm font-medium">Unlimited summaries for all attorneys</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300 text-sm">Unlimited seats - add your whole team</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300 text-sm">Shared workspaces & collections</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300 text-sm">Priority processing</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300 text-sm">Dedicated onboarding session</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300 text-sm">Priority support</span>
            </li>
          </ul>

          <button
            onClick={() => handleSalesContact('Firm')}
            className="w-full px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-400 transition-colors font-medium text-sm flex items-center justify-center gap-2"
          >
            <Mail className="w-4 h-4" />
            <span>Contact Sales</span>
          </button>
        </div>

      </div>

      {/* Value anchor + enterprise note */}
      <div className="mt-5 text-center space-y-1">
        <p className="text-gray-400 text-xs">
          At scale, the Firm plan works out to roughly half the cost of individual Pro seats -- and covers your entire practice.
        </p>
        <p className="text-gray-400 text-xs">
          Need SSO, custom invoicing, or a dedicated account manager?{' '}
          <button
            onClick={() => handleSalesContact('Enterprise')}
            className="text-gray-500 underline underline-offset-2 hover:text-gray-700 transition-colors"
          >
            Contact us about enterprise options.
          </button>
        </p>
      </div>

      {/* FAQ */}
      <div className="mt-16 max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Frequently Asked Questions</h3>
        </div>

        <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-white p-5">
            <h4 className="font-semibold text-gray-900 mb-1.5 text-sm">What counts as an AI comment summary?</h4>
            <p className="text-gray-500 text-sm">
              A summary is generated each time you ask CommsGenie to analyze the comment record for an FCC proceeding.
              This involves processing potentially hundreds of filings, which is why it is the gated resource across plans.
            </p>
          </div>

          <div className="bg-white p-5">
            <h4 className="font-semibold text-gray-900 mb-1.5 text-sm">What is always free, regardless of plan?</h4>
            <p className="text-gray-500 text-sm">
              Docket search, watchlist and filing alerts, and bulk filing download are free on every plan.
              Workspaces and team collaboration require Pro or above. Only AI-generated comment summaries are gated by plan tier.
            </p>
          </div>

          <div className="bg-white p-5">
            <h4 className="font-semibold text-gray-900 mb-1.5 text-sm">What happens when I reach my monthly summary limit?</h4>
            <p className="text-gray-500 text-sm">
              You will see a warning as you approach your limit. Once reached, you can upgrade &mdash; to Prosumer for
              more summaries or Pro for unlimited &mdash; or wait for your allowance to reset at the start of your next
              billing period.
            </p>
          </div>

          <div className="bg-white p-5">
            <h4 className="font-semibold text-gray-900 mb-1.5 text-sm">Can I change or cancel my plan?</h4>
            <p className="text-gray-500 text-sm">
              Yes. You can upgrade, downgrade, or cancel at any time with no cancellation fees.
              Changes take effect at the end of your current billing period.
            </p>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAuthModal(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Sign in"
            className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 shadow-xl"
          >
            <AuthForm
              onCancel={() => setShowAuthModal(false)}
              onSuccess={handleAuthSuccess}
              emailRedirectTo={`${window.location.origin}/pricing`}
            />
          </div>
        </div>
      )}

      {/* Stripe Error Modal */}
      {showStripeModal && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowStripeModal(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Checkout error"
            className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 shadow-xl"
          >
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Payment Error</h3>
              <p className="text-gray-500 text-sm">
                An error occurred while setting up payment. Please try again or contact us at sales@commsgenie.com.
              </p>
              <button
                onClick={() => setShowStripeModal(false)}
                className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Sales Modal */}
      {contactPlan && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setContactPlan(null); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Contact sales about the ${contactPlan} plan`}
            className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 shadow-xl"
          >
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Talk to sales about {contactPlan}</h3>
              <p className="text-gray-500 text-sm">
                Tell us about your team and we&apos;ll get you set up — usually a reply within one business day.
              </p>
              <a
                href={mailtoHref(contactPlan)}
                className="block w-full px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm"
              >
                Email {SALES_EMAIL}
              </a>
              <button
                type="button"
                onClick={handleCopyEmail}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                {copiedEmail ? 'Copied!' : 'Copy email address'}
              </button>
              <button
                type="button"
                onClick={() => setContactPlan(null)}
                className="text-gray-400 text-sm hover:text-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
